import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import type { WelcomeDepositBonus, DepositBonuses, DepositBonusesRaw, FreeRoundsBonuses, FreeRoundsBonusRaw, GroupedBonuses, GroupedBonusesRaw } from '@/types';
import { getAuthHeaders } from '@/utils/getHeaders';
import { bonusAdapter } from '@/adapters/bonusAdapter';
import { usePlayerStore } from './player';
import { isDepositBonusNoLimits, isDepositBonusWithLimits, isWelcomeDepositBonus } from '@/guards/bonusesGuards';

export const useBonusesStore = defineStore('bonuses', () => {
    const playerStore = usePlayerStore();
    const player = computed(() => playerStore.player);
    const isLockRequestGuard = ref(false);
    const bonuses = ref<GroupedBonuses<DepositBonuses, FreeRoundsBonuses>>({
        deposit: [],
        free_rounds: []
    });
    const triggerToRequestBonuses = ref(true);
    function toSetTriggerToRequestBonuses(flag: boolean) {
        triggerToRequestBonuses.value = flag;
    }
    const depositActiveBonuses = computed(() => {
        return bonuses.value.deposit.filter((bonus) => bonus.status !== 'created');
    });
    const freeRoundsActiveBonuses = computed(() => {
        return bonuses.value.free_rounds.filter((bonus) => bonus.status !== 'created');
    });
    const depositOnlyNotUsedPercentBonuses = computed(() => {
        return bonuses.value.deposit.filter((bonus) => bonus.bonus_percent && bonus.type !== 'no_deposit' && bonus.status === 'active');
    });
    const depositNotUsedBonuses = computed(() => {
        return bonuses.value.deposit.filter((bonus) => bonus.status === 'created');
    });
    const depositNotUsedPercentBonuses = computed(() => {
        return bonuses.value.deposit.filter((bonus) => bonus.bonus_percent && bonus.type !== 'no_deposit' && bonus.type !== 'welcome' && bonus.status === 'created');
    });
    const firstDepositWelcomeBonus = computed((): WelcomeDepositBonus | null => {
        const filtered = bonuses.value.deposit.filter((bonus) => bonus.status === 'created' && bonus.type === 'welcome');

        if (filtered.length === 0) return null;
        if (!isWelcomeDepositBonus(filtered[0])) return null;
        return filtered[0];
    });
    const freeRoundsNotUsedBonuses = computed(() => {
        return bonuses.value.free_rounds.filter((bonus) => bonus.status === 'created');
    });
    function toSetBonusFlip(bonus: DepositBonuses | FreeRoundsBonuses, flag: boolean, type: keyof GroupedBonuses<DepositBonuses, FreeRoundsBonuses>) {
        const index = bonuses.value[type]?.findIndex((item) => item.id === bonus.id);
        if (index !== undefined && index >= 0) {
            bonuses.value[type][index].flip = flag;
        }
    }
    function getBonusesWithRequestGuard(newBonusId: number) {
        if (!isLockRequestGuard.value) {
            isLockRequestGuard.value = true;
            setTimeout(() => {
                awaitGetMyBonuses(newBonusId);
            }, 1000);
        }
    }
    const awaitGetMyBonuses = async (newBonusId?: number) => {
        try {
            const { data, status } = await axios.get<GroupedBonusesRaw<DepositBonusesRaw, FreeRoundsBonusRaw>>('/main-api/bonuses/my', getAuthHeaders());

            if (status === 200) {
                const depositBonuses = {
                    withLimits: data.deposit.filter((item) => isDepositBonusWithLimits(item)).map((item) => bonusAdapter.mapDepositBonusLimits(item, player.value, newBonusId)),
                    noLimits: data.deposit.filter((item) => isDepositBonusNoLimits(item)).map((item) => bonusAdapter.mapDepositBonusNoLimits(item, newBonusId))
                };
                const deposit = [...depositBonuses.withLimits, ...depositBonuses.noLimits];

                const free_rounds = data.free_rounds.map((item) => bonusAdapter.mapFreeRoundsBonus(item, newBonusId));

                bonuses.value = {
                    deposit,
                    free_rounds
                };
                isLockRequestGuard.value = false;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitDeleteBonus = async (id: number, type: keyof GroupedBonuses<DepositBonuses, FreeRoundsBonuses>) => {
        try {
            const response = await axios.delete(`/main-api/bonuses/my/${id}`, getAuthHeaders());
            if (response.status === 200) {
                const index = bonuses.value[type]?.findIndex((bonus) => bonus.id === id);
                if (index !== undefined && index >= 0) {
                    bonuses.value[type]?.splice(index, 1);
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const awaitActivateBonus = async (id: number, type: keyof GroupedBonuses<DepositBonuses, FreeRoundsBonuses>) => {
        try {
            const response = await axios.patch(`/main-api/bonuses/${id}/activate`, null, getAuthHeaders());
            if (response.status === 204) {
                const bonus = bonuses.value[type]?.find((bonus) => bonus.id === id);
                if (bonus) {
                    bonus.status = 'active';
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    return {
        getBonusesWithRequestGuard,
        toSetTriggerToRequestBonuses,
        triggerToRequestBonuses,
        depositActiveBonuses,
        freeRoundsActiveBonuses,
        depositNotUsedBonuses,
        depositNotUsedPercentBonuses,
        freeRoundsNotUsedBonuses,
        depositOnlyNotUsedPercentBonuses,
        firstDepositWelcomeBonus,
        bonuses,
        awaitGetMyBonuses,
        toSetBonusFlip,
        awaitDeleteBonus,
        awaitActivateBonus
    };
});
